/**
 * coachFeedDb.js
 * Dexie (IndexedDB) database for CoachTheCoachDashboard CoachFeed.
 *
 * Replaces localStorage for the three large CoachFeed stores:
 *   • coachFeedItems   – cached coaching/leadership RSS articles, keyed by guid
 *   • coachFetchTimes  – per-topic last-fetch timestamps
 *   • coachFeedNotes   – per-article user notes, keyed by guid
 *
 * Everything else in CoachTheCoachDashboard (coachxcel notes, assessment cache,
 * skills cache, learning resources, categories) stays in localStorage — those
 * are small and never approach the quota limit.
 *
 * Usage:
 *   import {
 *     loadCoachFeedItems, saveCoachFeedItems,
 *     loadCoachFetchTimes, saveCoachFetchTime,
 *     loadCoachFeedNotes, saveCoachFeedNote,
 *     migrateCoachFeedFromLocalStorage,
 *   } from './coachFeedDb';
 */

import Dexie from 'dexie';

const db = new Dexie('CoachFeedDB');

db.version(1).stores({
  /**
   * coachFeedItems
   * Primary key : [userId+guid]  (compound — isolates users on shared devices)
   * Indexed     : userId, category_name  (fast per-user / per-topic queries)
   */
  coachFeedItems: '[userId+guid], userId, category_name, pub_date',

  /**
   * coachFetchTimes
   * Primary key : [userId+topic]
   */
  coachFetchTimes: '[userId+topic], userId',

  /**
   * coachFeedNotes
   * Primary key : [userId+guid]
   */
  coachFeedNotes: '[userId+guid], userId',
});

// ─────────────────────────────────────────────────────────────────────────────
//  coachFeedItems helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all coach feed items for a user as a { guid: item } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCoachFeedItems(userId) {
  const rows = await db.coachFeedItems.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row;
  });
  return map;
}

/**
 * Upsert (put) an array of coach feed articles for a user.
 * Each article must have a `guid`.
 * @param {string} userId
 * @param {Object[]} articles
 */
export async function saveCoachFeedItems(userId, articles) {
  const rows = articles.map((a) => ({ ...a, userId }));
  await db.coachFeedItems.bulkPut(rows);
}

// ─────────────────────────────────────────────────────────────────────────────
//  coachFetchTimes helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all fetch timestamps for a user as { topic: timestamp } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCoachFetchTimes(userId) {
  const rows = await db.coachFetchTimes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.topic] = row.timestamp;
  });
  return map;
}

/**
 * Upsert the fetch timestamp for a single topic.
 * @param {string} userId
 * @param {string} topic
 * @param {number} timestamp  – Date.now()
 */
export async function saveCoachFetchTime(userId, topic, timestamp) {
  await db.coachFetchTimes.put({ userId, topic, timestamp });
}

// ─────────────────────────────────────────────────────────────────────────────
//  coachFeedNotes helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load all notes for a user as { guid: noteText } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadCoachFeedNotes(userId) {
  const rows = await db.coachFeedNotes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row.text;
  });
  return map;
}

/**
 * Upsert a single coach feed note.
 * @param {string} userId
 * @param {string} guid
 * @param {string} text
 */
export async function saveCoachFeedNote(userId, guid, text) {
  await db.coachFeedNotes.put({ userId, guid, text });
}

/**
 * Delete a single coach feed note (called when user clears a note).
 * @param {string} userId
 * @param {string} guid
 */
export async function deleteCoachFeedNote(userId, guid) {
  await db.coachFeedNotes.delete([userId, guid]);
}

// ─────────────────────────────────────────────────────────────────────────────
//  One-time migration: localStorage → IndexedDB
//  Call once on mount; self-removes old keys when done.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Migrates legacy coachfeed localStorage data for this user into IndexedDB,
 * then removes the old keys to free quota.
 * Safe to call on every mount — no-op if the keys are already absent.
 * @param {string} userId  – the effectiveUserId used as the localStorage suffix
 */
export async function migrateCoachFeedFromLocalStorage(userId) {
  // ── feed items ──────────────────────────────────────────────────────────
  const rawItems = localStorage.getItem(`coachfeed_all_items_${userId}`);
  if (rawItems) {
    try {
      const itemMap = JSON.parse(rawItems); // { guid: article }
      const articles = Object.values(itemMap);
      if (articles.length > 0) {
        await saveCoachFeedItems(userId, articles);
      }
      localStorage.removeItem(`coachfeed_all_items_${userId}`);
      console.log(`[CoachFeedDB] Migrated ${articles.length} feed items for user ${userId}`);
    } catch (e) {
      console.warn('[CoachFeedDB] Could not migrate feed items:', e);
    }
  }

  // ── fetch timestamps ────────────────────────────────────────────────────
  const rawTimes = localStorage.getItem(`coachfeed_fetch_times_${userId}`);
  if (rawTimes) {
    try {
      const timesMap = JSON.parse(rawTimes); // { topic: timestamp }
      for (const [topic, timestamp] of Object.entries(timesMap)) {
        await saveCoachFetchTime(userId, topic, timestamp);
      }
      localStorage.removeItem(`coachfeed_fetch_times_${userId}`);
      console.log('[CoachFeedDB] Migrated fetch timestamps for user', userId);
    } catch (e) {
      console.warn('[CoachFeedDB] Could not migrate fetch times:', e);
    }
  }

  // ── notes ───────────────────────────────────────────────────────────────
  const rawNotes = localStorage.getItem(`coachfeed_notes_${userId}`);
  if (rawNotes) {
    try {
      const notesMap = JSON.parse(rawNotes); // { guid: text }
      for (const [guid, text] of Object.entries(notesMap)) {
        await saveCoachFeedNote(userId, guid, text);
      }
      localStorage.removeItem(`coachfeed_notes_${userId}`);
      console.log('[CoachFeedDB] Migrated notes for user', userId);
    } catch (e) {
      console.warn('[CoachFeedDB] Could not migrate notes:', e);
    }
  }
}

/**
 * Load all CoachFeed notes for a user as raw rows (not the collapsed
 * guid→text map loadCoachFeedNotes returns) — used by the backup/export engine.
 * @param {string} userId
 * @returns {Promise<Array<{userId: string, guid: string, text: string}>>}
 */
export async function getAllCoachFeedNotesForExport(userId) {
  return await db.coachFeedNotes.where('userId').equals(userId).toArray();
}

/**
 * Bulk-restore CoachFeed notes from a backup (raw rows, same shape as
 * getAllCoachFeedNotesForExport returns). Upserts by [userId+guid].
 * @param {Array<{userId: string, guid: string, text: string}>} rows
 */
export async function bulkPutCoachFeedNotes(rows) {
  if (!rows || rows.length === 0) return;
  await db.coachFeedNotes.bulkPut(rows);
}

export default db;